// How Threads counts the length of a post (OP#3466 / OP#3469, corrected in OP#3475): in Unicode code points,
// 500 at most. Threads' documentation says emoji count as their UTF-8 bytes, but that is not what the API
// does. Measured on the real API (unpublished containers): 500 thumbs-up and 250 thumbs-up with a skin tone
// (500 code points) are accepted; 501 thumbs-up, 251 with a skin tone (502 code points) and 300 times
// `e` + a combining accent (600 code points) are refused. The server applies the same rule
// (server/app/Services/SocialApi/ThreadsTextLength.php) with the same test vectors
// (src/utils/__tests__/threadsText.test.js): change both together.

/**
 * Length of a text as Threads counts it: its Unicode code points (`Array.from` walks code points, so a
 * skin tone, a joiner and each half of a flag count as one each).
 *
 * @param {string} text - Post text, with the hashtags appended when it is part 1.
 * @returns {number} The length Threads checks against its 500 limit.
 */
export function threadsTextLength(text) {
  if (typeof text !== "string" || text === "") return 0;
  return Array.from(text).length;
}

/**
 * Hashtags as they are appended to part 1's text ("#a #b").
 *
 * @param {Array<string>} tags - Tags from the composer's tag field, with or without "#".
 * @returns {string} The tags joined by spaces.
 */
export function formatTagsForText(tags) {
  if (!Array.isArray(tags)) return "";
  return tags
    .filter((t) => typeof t === "string" && t !== "")
    .map((t) => (t.startsWith("#") ? t : `#${t}`))
    .join(" ");
}

/**
 * Part 1's text exactly as it is sent: the text, a space and the hashtags.
 *
 * @param {string} text - The composer text.
 * @param {string} formattedTags - Output of formatTagsForText.
 * @returns {string} The text sent as `type_data.text`.
 */
export function textWithTags(text, formattedTags) {
  return `${text || ""} ${formattedTags || ""}`.trim();
}

// Unique links in a text, the way Meta's 5-link limit is read on our side (the server / Meta stay the
// authority).
const LINK = /(https?:\/\/|www\.)\S+/gi;

/**
 * Number of different links in a text.
 *
 * @param {string} text - Post text.
 * @returns {number} Count of unique URLs.
 */
export function countUniqueLinks(text) {
  if (typeof text !== "string" || text === "") return 0;
  const found = text.match(LINK) || [];
  return new Set(found.map((u) => u.toLowerCase())).size;
}
